import type { AdminAccountBilling, CommissionSightClient } from '@commissionsight/sdk';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { AccountAccessPanel } from '../components/AccountAccessPanel.js';
import { AiUsageTile } from '../components/AiUsageTile.js';
import { EntitlementsPanel } from '../components/EntitlementsPanel.js';
import { InboundAliasesPanel } from '../components/InboundAliasesPanel.js';
import { InsightsPanel } from '../components/InsightsPanel.js';
import { ProcessingToggles } from '../components/ProcessingToggles.js';
import { RateSchedulesPanel } from '../components/RateSchedulesPanel.js';
import { WorkspacesPanel } from '../components/WorkspacesPanel.js';
import {
  Badge,
  ErrorNote,
  IconButton,
  Metric,
  type Notify,
  Pager,
  Panel,
  SortHeader,
  btn,
  btnGhost,
  input,
  timeAgo,
  useRun,
  useSort,
} from '../components/ui.js';
import { assertProvisioned, fmtWhen } from '../lib/format.js';
import { deleteAccount, getAccountAudit } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { useClientPaged } from '../lib/usePaged.js';

type Run = (
  fn: () => Promise<unknown>,
  opts?: { confirm?: string; success?: string },
) => Promise<boolean>;

export function Accounts({ client, notify }: { client: CommissionSightClient; notify: Notify }) {
  const run = useRun(notify);
  const { data, error, reload } = useAsyncData(
    useCallback(() => client.admin.listAccounts().then((r) => r.data), [client]),
  );
  // Engagement fields the API now returns per account (merged from users.last_seen_at);
  // typed locally so the view reads them without an SDK release.
  const accounts = (data ?? []) as (NonNullable<typeof data>[number] & {
    totalUsers?: number;
    active7d?: number;
    active30d?: number;
    lastActiveAt?: number | null;
  })[];
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  const [rate, setRate] = useState<Record<string, string>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminAccountBilling | null>(null);
  // Default to most-recently-active first → engaged agencies up top, quiet ones sink.
  const { sorted, sortKey, sortDir, toggle } = useSort(accounts, {
    key: 'lastActiveAt',
    dir: 'desc',
  });
  const paged = useClientPaged(sorted, 20);

  // Seed the editable rate inputs whenever the accounts list (re)loads.
  useEffect(() => {
    if (!data) return;
    setRate(
      Object.fromEntries(
        data.map((a) => [
          a.id,
          a.customRateCents != null ? (a.customRateCents / 100).toString() : '',
        ]),
      ),
    );
  }, [data]);

  const toggleDetail = async (id: string) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    try {
      setDetail(await client.admin.getAccountBilling(id));
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to load billing', 'error');
    }
  };

  return (
    <Panel title="Accounts">
      <div className="mb-4 flex gap-2">
        <input
          className={input}
          placeholder="New account name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="button"
          className={btn}
          onClick={() =>
            run(
              async () => {
                if (!name) return;
                await client.admin.createAccount(name);
                setName('');
                reload();
              },
              { success: 'Account created' },
            )
          }
        >
          Create
        </button>
      </div>
      {issued && (
        <p className="mb-3 rounded bg-amber-50 px-3 py-2 font-mono text-xs text-amber-800">
          Token (shown once): {issued}
        </p>
      )}
      {error && (
        <div className="mb-3">
          <ErrorNote message={error} onRetry={reload} />
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-neutral-500">
          <tr>
            {(() => {
              const sp = { sortKey, sortDir, onSort: toggle };
              return (
                <>
                  <SortHeader label="Name" k="name" {...sp} className="py-1" />
                  <th>Slug</th>
                  <SortHeader label="Status" k="status" {...sp} />
                  <SortHeader label="Active 7d" k="active7d" {...sp} />
                  <SortHeader label="Active 30d" k="active30d" {...sp} />
                  <SortHeader label="Last active" k="lastActiveAt" {...sp} />
                  <th>Custom $/member</th>
                  <th />
                </>
              );
            })()}
          </tr>
        </thead>
        <tbody>
          {paged.pageItems.map((a) => (
            <Fragment key={a.id}>
              <tr className="border-t border-neutral-100">
                <td className="py-2">{a.name}</td>
                <td className="font-mono text-xs">{a.slug}</td>
                <td>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      a.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : a.status === 'pending'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-rose-100 text-rose-700'
                    }`}
                  >
                    {a.status}
                  </span>
                  {a.provisioned === false && (
                    <span
                      className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                      title="No data store provisioned — uploads are rejected until this is set up."
                    >
                      not provisioned
                    </span>
                  )}
                </td>
                <td className="tabular-nums text-neutral-600">
                  {a.active7d ?? 0}
                  <span className="text-neutral-400">/{a.totalUsers ?? 0}</span>
                </td>
                <td className="tabular-nums text-neutral-600">
                  {a.active30d ?? 0}
                  <span className="text-neutral-400">/{a.totalUsers ?? 0}</span>
                </td>
                <td
                  className="text-neutral-600"
                  title={a.lastActiveAt ? new Date(a.lastActiveAt).toLocaleString() : 'never'}
                >
                  {timeAgo(a.lastActiveAt)}
                </td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <input
                      className="w-20 rounded border border-neutral-200 px-2 py-1 text-xs"
                      placeholder="tier"
                      value={rate[a.id] ?? ''}
                      onChange={(e) => setRate((m) => ({ ...m, [a.id]: e.target.value }))}
                    />
                    <IconButton
                      icon="save"
                      label="Set custom rate"
                      tone="emerald"
                      onClick={() =>
                        run(
                          async () => {
                            const raw = (rate[a.id] ?? '').trim();
                            const cents = raw === '' ? null : Math.round(Number(raw) * 100);
                            if (cents !== null && (!Number.isFinite(cents) || cents < 0)) return;
                            await client.admin.setBillingRate(a.id, cents);
                            reload();
                          },
                          { success: 'Rate updated' },
                        )
                      }
                    />
                  </div>
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <IconButton
                      icon={openId === a.id ? 'collapse' : 'view'}
                      label={openId === a.id ? 'Hide details' : 'Details'}
                      onClick={() => toggleDetail(a.id)}
                    />
                    {a.status === 'pending' && (
                      <IconButton
                        icon="approve"
                        label={`Approve ${a.name}`}
                        tone="emerald"
                        onClick={() =>
                          run(
                            async () => {
                              const res = await client.admin.approveAccount(a.id).finally(reload);
                              assertProvisioned(res.provision); // throws → error toast
                            },
                            {
                              confirm: `Approve “${a.name}”? They get immediate access.`,
                              success: 'Account approved',
                            },
                          )
                        }
                      />
                    )}
                    <IconButton
                      icon="token"
                      label={`Issue API token for ${a.name}`}
                      tone="indigo"
                      onClick={() =>
                        run(
                          async () => {
                            const res = await client.admin.issueToken(a.id, 'primary');
                            setIssued(res.token);
                          },
                          {
                            confirm: `Issue a new API token for “${a.name}”? It is shown only once.`,
                          },
                        )
                      }
                    />
                    <IconButton
                      icon="database"
                      label={`Purge raw files for ${a.name}`}
                      tone="amber"
                      onClick={() =>
                        run(
                          async () => {
                            const res = await client.admin.purgeAccountFiles(a.id);
                            notify(`Purged ${res.purged} raw file(s) from storage`, 'success');
                          },
                          {
                            confirm: `Purge ALL raw statement files for “${a.name}” from storage? Scored results stay, but the raw files can no longer be re-processed. This can't be undone.`,
                          },
                        )
                      }
                    />
                    <IconButton
                      icon="remove"
                      label={`Delete account ${a.name}`}
                      tone="rose"
                      onClick={() =>
                        run(
                          async () => {
                            const res = await deleteAccount(a.id);
                            notify(
                              `Deleted “${res.name}” — ${res.usersRemoved} user(s) and all data removed (${res.filesPurged} file(s) purged)`,
                              'success',
                            );
                            setOpenId(null);
                            reload();
                          },
                          {
                            confirm: `PERMANENTLY delete “${a.name}” and ALL its users, tokens, files, jobs, rates, and data? This cannot be undone.`,
                          },
                        )
                      }
                    />
                  </div>
                </td>
              </tr>
              {openId === a.id && (
                <tr className="bg-neutral-50">
                  <td colSpan={8} className="px-4 py-4">
                    <ExpandedAccount
                      a={a}
                      client={client}
                      detail={detail}
                      rateValue={rate[a.id] ?? ''}
                      onRateChange={(v) => setRate((m) => ({ ...m, [a.id]: v }))}
                      onDetail={setDetail}
                      run={run}
                      reload={reload}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      <Pager
        offset={paged.offset}
        count={paged.count}
        total={paged.total}
        hasMore={paged.hasMore}
        onPrev={paged.prev}
        onNext={paged.next}
      />
    </Panel>
  );
}

type DetailTab = 'activity' | 'billing' | 'features' | 'infra' | 'audit';

/**
 * The expanded account view, organized into tabs (Activity / Billing / Features /
 * Infrastructure) in a wider, sectioned layout — instead of every panel stacked
 * into one long expanded row.
 */
function ExpandedAccount({
  a,
  client,
  detail,
  rateValue,
  onRateChange,
  onDetail,
  run,
  reload,
}: {
  a: { id: string; name: string; status: string; provisioned?: boolean };
  client: CommissionSightClient;
  detail: AdminAccountBilling | null;
  rateValue: string;
  onRateChange: (v: string) => void;
  onDetail: (b: AdminAccountBilling) => void;
  run: Run;
  reload: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>('activity');
  const TABS: { key: DetailTab; label: string }[] = [
    { key: 'activity', label: 'Activity' },
    { key: 'billing', label: 'Billing' },
    { key: 'features', label: 'Features & rates' },
    { key: 'infra', label: 'Data & tokens' },
    { key: 'audit', label: 'Audit' },
  ];
  const pill = (active: boolean) =>
    `rounded-full px-3.5 py-1.5 text-xs font-medium transition ${active ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:bg-neutral-100'}`;

  const saveRate = () =>
    run(
      async () => {
        const raw = rateValue.trim();
        const cents = raw === '' ? null : Math.round(Number(raw) * 100);
        if (cents !== null && (!Number.isFinite(cents) || cents < 0)) return;
        await client.admin.setBillingRate(a.id, cents);
        onDetail(await client.admin.getAccountBilling(a.id));
        reload();
      },
      { success: 'Rate updated' },
    );
  const toggleSurcharge = (enabled: boolean) =>
    run(
      async () => {
        await client.admin.setSurcharge(a.id, enabled);
        onDetail(await client.admin.getAccountBilling(a.id));
      },
      { success: 'Surcharge updated' },
    );

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={pill(tab === t.key)}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'activity' && <AccountActivity accountId={a.id} client={client} />}

      {tab === 'billing' && (
        <AccountDetail
          billing={detail}
          rateValue={rateValue}
          onRateChange={onRateChange}
          onSaveRate={saveRate}
          onToggleSurcharge={toggleSurcharge}
        />
      )}

      {tab === 'features' && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <AccountAccessPanel account={a} onChanged={reload} />
            <ProcessingToggles accountId={a.id} />
            <AiUsageTile accountId={a.id} />
            <EntitlementsPanel accountId={a.id} />
            <WorkspacesPanel accountId={a.id} />
            <InsightsPanel accountId={a.id} />
          </div>
          <RateSchedulesPanel accountId={a.id} />
          <InboundAliasesPanel accountId={a.id} />
        </div>
      )}

      {tab === 'infra' && (
        <div className="space-y-4">
          <DataStoreSection account={a} client={client} run={run} onDone={reload} />
          <TokensSection accountId={a.id} client={client} run={run} />
        </div>
      )}

      {tab === 'audit' && <AccountAudit accountId={a.id} />}
    </div>
  );
}

/** Account-action audit: who did what to this account (provision, pause, rate
 *  change, impersonate, file purge…). Loaded on demand. */
function AccountAudit({ accountId }: { accountId: string }) {
  const { data, error } = useAsyncData(useCallback(() => getAccountAudit(accountId), [accountId]));
  if (error) return <p className="text-sm text-rose-600">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (data.length === 0)
    return <p className="text-sm text-neutral-400">No recorded actions for this account yet.</p>;
  const when = (t: number | string) => new Date(t).toLocaleString('en-US');
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-neutral-500">
        <tr>
          <th className="py-1">When</th>
          <th>Action</th>
          <th>Target</th>
          <th>Actor</th>
          <th>IP</th>
        </tr>
      </thead>
      <tbody>
        {data.map((e) => (
          <tr key={e.id} className="border-t border-neutral-100">
            <td className="py-1.5 text-neutral-600">{when(e.createdAt)}</td>
            <td className="font-mono text-xs text-neutral-700">{e.action}</td>
            <td className="truncate text-neutral-600">{e.target ?? '—'}</td>
            <td className="text-neutral-600">{e.actor}</td>
            <td className="font-mono text-xs text-neutral-500">{e.ip ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const money = (v: number | null | undefined) =>
  v == null
    ? '—'
    : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const period = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

/** Per-account dashboard + drill-down into the account's files, jobs, and users. */
function AccountActivity({
  accountId,
  client,
}: { accountId: string; client: CommissionSightClient }) {
  const { data: ov, error } = useAsyncData(
    useCallback(() => client.admin.accountOverview(accountId), [client, accountId]),
  );
  const [sub, setSub] = useState<'files' | 'jobs' | 'users'>('files');
  const tabCls = (active: boolean) =>
    `rounded-full px-3 py-1 text-xs font-medium ${active ? 'bg-emerald-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`;

  return (
    <div className="mb-4 rounded-lg border border-neutral-200 bg-white p-4">
      <h4 className="mb-3 text-sm font-semibold">
        Activity{ov?.latestPeriod ? ` · latest ${ov.latestPeriod}` : ''}
      </h4>
      {error && <ErrorNote message={error} />}
      {!ov ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric label="Members" value={ov.rollup?.memberCount ?? 0} />
            <Metric label="Owed" value={money(ov.rollup?.commissionOwed)} tone="emerald" />
            <Metric label="At risk" value={money(ov.rollup?.commissionAtRisk)} tone="rose" />
            <Metric
              label="Chargebacks"
              value={
                ov.rollup
                  ? `${ov.rollup.chargebackCount} · ${money(ov.rollup.chargebackAmount)}`
                  : '—'
              }
              tone="amber"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
            <span>{ov.counts.files.toLocaleString()} files</span>
            <span>{ov.counts.jobs.toLocaleString()} jobs</span>
            <span>{ov.counts.users.toLocaleString()} users</span>
            {ov.rollup && (
              <span>
                {ov.rollup.green}🟢 {ov.rollup.yellow}🟡 {ov.rollup.red}🔴 · attrition{' '}
                {(ov.rollup.attritionRate * 100).toFixed(1)}%
              </span>
            )}
          </div>
          <div className="mt-4 flex gap-1.5">
            {(['files', 'jobs', 'users'] as const).map((t) => (
              <button key={t} type="button" className={tabCls(sub === t)} onClick={() => setSub(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <div className="mt-3">
            {sub === 'files' && <FilesTable accountId={accountId} client={client} />}
            {sub === 'jobs' && <JobsTable accountId={accountId} client={client} />}
            {sub === 'users' && <UsersTable accountId={accountId} client={client} />}
          </div>
        </>
      )}
    </div>
  );
}

function FilesTable({ accountId, client }: { accountId: string; client: CommissionSightClient }) {
  const { data, error } = useAsyncData(
    useCallback(
      () => client.admin.accountFiles(accountId).then((r) => r.data),
      [client, accountId],
    ),
  );
  if (error) return <ErrorNote message={error} />;
  if (!data) return <p className="text-xs text-neutral-400">Loading…</p>;
  if (data.length === 0) return <p className="text-xs text-neutral-400">No files.</p>;
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-neutral-500">
        <tr>
          <th className="py-1">File</th>
          <th>Period</th>
          <th>Size</th>
          <th>Uploaded</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {data.map((f) => (
          <tr key={f.id} className="border-t border-neutral-100">
            <td className="py-1.5">{f.originalFilename}</td>
            <td>{period(f.periodYear, f.periodMonth)}</td>
            <td>{(f.byteSize / 1024).toFixed(0)} KB</td>
            <td className="text-neutral-400">{fmtWhen(f.uploadedAt)}</td>
            <td className="text-right">
              {f.supersededAt && <Badge tone="amber">superseded</Badge>}{' '}
              {f.r2PurgedAt && <Badge tone="neutral">purged</Badge>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function JobsTable({ accountId, client }: { accountId: string; client: CommissionSightClient }) {
  const { data, error } = useAsyncData(
    useCallback(() => client.admin.accountJobs(accountId).then((r) => r.data), [client, accountId]),
  );
  if (error) return <ErrorNote message={error} />;
  if (!data) return <p className="text-xs text-neutral-400">Loading…</p>;
  if (data.length === 0) return <p className="text-xs text-neutral-400">No jobs.</p>;
  const tone = (s: string) =>
    s === 'completed'
      ? 'emerald'
      : s === 'failed'
        ? 'rose'
        : s === 'processing'
          ? 'sky'
          : 'neutral';
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-neutral-500">
        <tr>
          <th className="py-1">Period</th>
          <th>Status</th>
          <th>Scored</th>
          <th>Created</th>
        </tr>
      </thead>
      <tbody>
        {data.map((j) => (
          <tr key={j.id} className="border-t border-neutral-100">
            <td className="py-1.5">{period(j.periodYear, j.periodMonth)}</td>
            <td>
              <Badge tone={tone(j.status)}>{j.status}</Badge>
            </td>
            <td className="text-neutral-500">
              {j.stats
                ? `${j.stats.green ?? 0}🟢 ${j.stats.yellow ?? 0}🟡 ${j.stats.red ?? 0}🔴`
                : j.error
                  ? '—'
                  : ''}
            </td>
            <td className="text-neutral-400">{fmtWhen(j.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UsersTable({ accountId, client }: { accountId: string; client: CommissionSightClient }) {
  const { data, error } = useAsyncData(
    useCallback(
      () => client.admin.accountUsers(accountId).then((r) => r.data),
      [client, accountId],
    ),
  );
  if (error) return <ErrorNote message={error} />;
  if (!data) return <p className="text-xs text-neutral-400">Loading…</p>;
  if (data.length === 0) return <p className="text-xs text-neutral-400">No users.</p>;
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-neutral-500">
        <tr>
          <th className="py-1">Email</th>
          <th>Role</th>
          <th>Joined</th>
        </tr>
      </thead>
      <tbody>
        {data.map((u) => (
          <tr key={u.id} className="border-t border-neutral-100">
            <td className="py-1.5">{u.email}</td>
            <td>{u.role}</td>
            <td className="text-neutral-400">{fmtWhen(u.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DataStoreSection({
  account,
  client,
  run,
  onDone,
}: {
  account: { id: string; name: string; provisioned?: boolean };
  client: CommissionSightClient;
  run: Run;
  onDone: () => void;
}) {
  const [connString, setConnString] = useState('');
  // Once an account has a data store, provisioning is locked — re-provisioning
  // would risk pointing it at a different (empty) database. Both controls disable.
  const isProvisioned = account.provisioned !== false;
  return (
    <div className="mt-4 rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-1 flex items-center gap-2">
        <h4 className="text-sm font-semibold">Data store</h4>
        {isProvisioned ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
            provisioned
          </span>
        ) : (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            not provisioned
          </span>
        )}
      </div>
      <p className="mb-3 text-xs text-neutral-500">
        {isProvisioned
          ? 'This account already has a dedicated database. Provisioning is disabled to protect its data.'
          : 'Each account needs its own database before it can process uploads. Auto-provision creates one (requires Neon setup), or paste a connection string for a database you created.'}
      </p>
      <button
        type="button"
        className={btn}
        disabled={isProvisioned}
        onClick={() =>
          run(
            async () => {
              const res = await client.admin.provisionAccount(account.id);
              if (!res.provisioned) throw new Error(res.error ?? 'Provisioning failed');
              onDone();
            },
            {
              confirm: `Auto-provision a database for “${account.name}”?`,
              success: 'Account provisioned',
            },
          )
        }
      >
        Provision now (auto)
      </button>
      <div className="mt-3">
        <span className="mb-1 block text-xs font-medium text-neutral-600">
          Or provision with a connection string
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="min-w-[20rem] flex-1 rounded border border-neutral-300 px-2 py-1 font-mono text-xs disabled:bg-neutral-50 disabled:opacity-60"
            placeholder="postgresql://user:pass@host/db?sslmode=require"
            value={connString}
            disabled={isProvisioned}
            onChange={(e) => setConnString(e.target.value)}
          />
          <button
            type="button"
            className={btnGhost}
            disabled={isProvisioned || !connString.trim()}
            onClick={() =>
              run(
                async () => {
                  const res = await client.admin.provisionAccount(account.id, connString.trim());
                  if (!res.provisioned) throw new Error(res.error ?? 'Provisioning failed');
                  setConnString('');
                  onDone();
                },
                {
                  confirm: `Provision “${account.name}” with this connection string? It will be migrated and stored encrypted.`,
                  success: 'Account provisioned',
                },
              )
            }
          >
            Provision with connection string
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountDetail({
  billing,
  rateValue,
  onRateChange,
  onSaveRate,
  onToggleSurcharge,
}: {
  billing: AdminAccountBilling | null;
  rateValue: string;
  onRateChange: (v: string) => void;
  onSaveRate: () => void;
  onToggleSurcharge: (enabled: boolean) => void;
}) {
  if (!billing) return <p className="text-sm text-neutral-500">Loading…</p>;
  const addr = [
    billing.addressLine1,
    billing.addressLine2,
    [billing.city, billing.state].filter(Boolean).join(', '),
    billing.postalCode,
    billing.country,
  ]
    .filter(Boolean)
    .join(' · ');
  const row = (label: string, value: string | null) => (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-neutral-500">{label}</span>
      <span className="text-right font-medium">{value || '—'}</span>
    </div>
  );
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <h4 className="mb-2 text-sm font-semibold">Billing contact</h4>
        <div className="text-sm">
          {row('Name', billing.contactName)}
          {row('Company', billing.companyName)}
          {row('Email', billing.email)}
          {row('Phone', billing.phone)}
          {row('Address', addr || null)}
        </div>
      </div>
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <h4 className="mb-2 text-sm font-semibold">Pricing & payment</h4>
        <div className="text-sm">
          {row(
            'Method on file',
            billing.card
              ? `${billing.card.brand} •••• ${billing.card.last4}${billing.paymentMethodType === 'us_bank_account' ? ' (ACH)' : ''}`
              : null,
          )}
          {row('Last invoiced', billing.lastBilledPeriod)}
          {row(
            'Last amount',
            billing.lastBilledAmountCents != null
              ? `$${(billing.lastBilledAmountCents / 100).toLocaleString()}`
              : null,
          )}
        </div>
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <span className="mb-1 block text-xs font-medium text-neutral-600">
            Custom rate ($/member) — overrides volume tiers
          </span>
          <div className="flex items-center gap-2">
            <input
              className="w-28 rounded border border-neutral-300 px-2 py-1 text-sm"
              placeholder="volume tiers"
              value={rateValue}
              onChange={(e) => onRateChange(e.target.value)}
            />
            <button
              type="button"
              className="rounded-md bg-emerald-600 px-3 py-1 text-sm font-medium text-white hover:bg-emerald-700"
              onClick={onSaveRate}
            >
              Save
            </button>
            <span className="text-xs text-neutral-400">blank = standard tiers</span>
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 border-t border-neutral-100 pt-3 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-emerald-600"
            checked={billing.surchargeEnabled}
            onChange={(e) => onToggleSurcharge(e.target.checked)}
          />
          <span>Pass payment processing fee to customer</span>
        </label>
        <p className="mt-1 text-xs text-neutral-400">
          Off = CommissionSight absorbs the card/ACH fee for this account.
        </p>
      </div>
    </div>
  );
}

function TokensSection({
  accountId,
  client,
  run,
}: { accountId: string; client: CommissionSightClient; run: Run }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => client.admin.listTokens(accountId).then((r) => r.data), [client, accountId]),
  );
  const tokens = data ?? [];
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  return (
    <div className="mt-4 rounded-lg border border-neutral-200 bg-white p-4">
      <h4 className="mb-1 text-sm font-semibold">API access (integration tokens)</h4>
      <p className="mb-3 text-xs text-neutral-500">
        Partners call the API/SDK with these. Without an active token here, a downloaded SDK can do
        nothing — revoke to cut off an integration instantly.
      </p>
      {issued && (
        <p className="mb-2 break-all rounded bg-amber-50 px-3 py-2 font-mono text-xs text-amber-800">
          New token (shown once): {issued}
        </p>
      )}
      {error && (
        <div className="mb-2">
          <ErrorNote message={error} onRetry={reload} />
        </div>
      )}
      <table className="w-full text-sm">
        <tbody>
          {tokens.map((t) => (
            <tr key={t.id} className="border-t border-neutral-100">
              <td className="py-1.5">
                {t.label ?? '—'}
                {t.revoked && (
                  <span className="ml-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                    revoked
                  </span>
                )}
              </td>
              <td className="text-xs text-neutral-400">created {fmtWhen(t.createdAt)}</td>
              <td className="text-xs text-neutral-400">
                {t.lastUsedAt ? `used ${fmtWhen(t.lastUsedAt)}` : 'never used'}
              </td>
              <td className="text-right">
                {!t.revoked && (
                  <IconButton
                    icon="revoke"
                    label="Revoke token"
                    tone="rose"
                    onClick={() =>
                      run(
                        async () => {
                          await client.admin.revokeToken(t.id);
                          reload();
                        },
                        {
                          confirm:
                            'Revoke this token? Any integration using it stops working immediately.',
                          success: 'Token revoked',
                        },
                      )
                    }
                  />
                )}
              </td>
            </tr>
          ))}
          {tokens.length === 0 && !error && (
            <tr>
              <td className="py-2 text-xs text-neutral-400" colSpan={4}>
                No tokens issued.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2">
        <input
          className="w-44 rounded border border-neutral-300 px-2 py-1 text-xs"
          placeholder="label (e.g. Partner X)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <button
          type="button"
          className={btnGhost}
          onClick={() =>
            run(
              async () => {
                const res = await client.admin.issueToken(accountId, label || undefined);
                setIssued(res.token);
                setLabel('');
                reload();
              },
              { confirm: 'Issue a new API token? It is shown only once.' },
            )
          }
        >
          Issue token
        </button>
      </div>
    </div>
  );
}
